/* eslint-disable */
import type { Money } from "../../../../common/currency.pb";
import type { InstrumentType } from "../../../../common/instrument.pb";
import type { GlobalAddress } from "../../../../common/location.pb";
import type {
  Instrument,
  InstrumentSelection,
  RecurringPaymentBlockerButtonAction,
  RecurringPaymentBlockerSchedule,
} from "../../../../franklin/api.pb";
import type { MultiCurrencyAmountEntryBlockerFocusedCurrencyField } from "../../../../franklin/blockers/multi_currency_amount_entry_blocker.pb";
import type { BrowserInteraction } from "../../../../franklin/browser_interaction.pb";
import type { AppVersion } from "../../../../franklin/common.pb";
import type { CardScanResult } from "../../../../franklin/experiments/bouncer.pb";
import type { LinkResult } from "../../../../franklin/link_result.pb";
import type {
  ThreeDomainSecureV2ActionError,
  ThreeDomainSecureV2ActionSuccess,
} from "../../../../franklin/rpc/handle_three_domain_secure_v2_action.pb";
import type { UiCustomer } from "../../../../franklin/ui/customer.pb";
import type {
  PinwheelLinkOnExitResponse,
  PinwheelLinkOnLoginResponse,
  PinwheelLinkOnSuccessResponse,
} from "../../../../franklin/ui/pinwheel_link.pb";
import type { DirectDepositTransferSelectionRequestAccountTransferSelection } from "../../../../instant-pay/instantpay_api.pb";
import type { PayrollProvider } from "../../../../payroll-connector/common/PayrollProviderProto.pb";
import type { GroupParticipant } from "../../../groups/group.pb";
import type { Automation } from "../../../investautomator/model/automation.pb";
import type { SetPaycheckAllocationAmountInputs } from "../../../paychecks/api/blockers/v1/set_paycheck_allocation_amount.pb";
import type { InstrumentSelectionOption } from "../../common/InstrumentSelectionOption.pb";
import type { ScientificNumber } from "../../common/ScientificNumber.pb";

/**
 * Data structure to encapsulate ONLY user inputs collected from a blocker. This is what clients
 * send to the server and what Plasma exposes to requirement handlers.
 */
export interface BlockerInputs {
  /** used for idempotence */
  blocker_descriptor_id?: string;
  /** @deprecated */
  confirm_passcode_inputs?:
    | ConfirmPasscodeInputs
    | undefined;
  /** @deprecated */
  confirm_inputs?: ConfirmInputs | undefined;
  passcode_creation_inputs?: PasscodeCreationInputs | undefined;
  phone_verification_inputs?: PhoneVerificationInputs | undefined;
  select_option_inputs?: SelectOptionInputs | undefined;
  verify_identity_inputs?: VerifyIdentityInputs | undefined;
  physical_card_qr_code_verification_inputs?: PhysicalCardVerificationQrCodeInputs | undefined;
  physical_card_cvv_and_expiration_verification_inputs?: PhysicalCardVerificationCvvAndExpirationInputs | undefined;
  form_inputs?: FormInputs | undefined;
  verify_passcode_inputs?: VerifyPasscodeInputs | undefined;
  create_card_design_inputs?: CreateCardDesignInputs | undefined;
  register_sms_inputs?: RegisterSmsInputs | undefined;
  register_email_inputs?: RegisterEmailInputs | undefined;
  upload_file_inputs?: UploadFileInputs | undefined;
  verify_government_id_inputs?: VerifyGovernmentIdInputs | undefined;
  name_on_card_inputs?: NameOnCardInputs | undefined;
  upload_check_amount_and_images_inputs?: UploadCheckAmountAndImagesInputs | undefined;
  link_card_input?: LinkCardInput | undefined;
  amount_input?: AmountInput | undefined;
  signature_input?: SignatureInput | undefined;
  rate_plan_input?: RatePlanInput | undefined;
  confirm_payment_inputs?: ConfirmPaymentInputs | undefined;
  address_inputs?: AddressInputs | undefined;
  select_sponsors_inputs?: SelectSponsorsInputs | undefined;
  finish_payroll_provider_authentication_inputs?: FinishPayrollProviderAuthenticationInputs | undefined;
  password_creation_inputs?: PasswordCreationInputs | undefined;
  password_verification_inputs?: PasswordVerificationInputs | undefined;
  search_payroll_providers_inputs?: SearchPayrollProvidersInputs | undefined;
  finish_instant_pay_auto_advance_inputs?: FinishInstantPayAutoAdvanceInputs | undefined;
  select_shipping_option_inputs?: SelectShippingOptionInputs | undefined;
  configure_round_up_inputs?: ConfigureRoundUpInputs | undefined;
  persona_didv_inputs?: PersonaDidvInputs | undefined;
  activity_picker_inputs?: ActivityPickerInputs | undefined;
  provision_digital_wallet_token_inputs?: ProvisionDigitalWalletTokenInputs | undefined;
  reward_code_input?: RewardCodeInput | undefined;
  fileset_upload_inputs?: FilesetUploadInputs | undefined;
  activate_digital_wallet_token_inputs?: ActivateDigitalWalletTokenInputs | undefined;
  instrument_selection_inputs?: InstrumentSelectionInputs | undefined;
  link_new_bank_account_inputs?: LinkNewBankAccountInputs | undefined;
  skip_input?: SkipInput | undefined;
  end_flow_inputs?: EndFlowInputs | undefined;
  webview_callback_inputs?: WebViewCallbackInputs | undefined;
  configure_iwys_inputs?: ConfigureIWYSInputs | undefined;
  three_domain_secure_redirect_inputs?: ThreeDomainSecureRedirectInputs | undefined;
  three_domain_secure_native_inputs?: ThreeDomainSecureNativeInputs | undefined;
  instant_pay_direct_deposit_transfer_selection_inputs?: InstantPayDirectDepositTransferSelectionInputs | undefined;
  participant_selection_inputs?: ParticipantSelectionInputs | undefined;
  multi_currency_amount_entry_inputs?: MultiCurrencyAmountEntryBlockerInputs | undefined;
  clabe_entry_inputs?: ClabeEntryInputs | undefined;
  set_recurring_payment_preferences_inputs?: SetRecurringPaymentPreferencesInputs | undefined;
  overflow_option_picker_inputs?: OverflowOptionPickerBlockerInputs | undefined;
  multi_currency_payment_review_inputs?: MultiCurrencyPaymentReviewInputs | undefined;
  link_card_v2_inputs?: LinkCardV2Inputs | undefined;
  recipient_selector_inputs?: RecipientSelectorInputs | undefined;
  set_paycheck_allocation_amount_inputs?: SetPaycheckAllocationAmountInputs | undefined;
  select_investment_entity_inputs?:
    | SelectInvestmentEntityInputs
    | undefined;
  /** blockers with the same group id will be sent to the server together */
  group_id?: string;
}

export interface LinkNewBankAccountInputs {
  /** Url containing the oauth flow response. */
  url_contents?: string;
}

/**
 * Customer is confirming to something, could be a payment.
 *
 * @deprecated
 */
export interface ConfirmInputs {
  instrument_token?: string;
  /**
   * The fee amount that the user has confirmed will be applied. If the fee ends up being
   * higher than this amount, the server must ask for confirmation or fail the payment.
   */
  accepted_fee_amount?: Money;
  /**
   * For claimable movements, the original and active customer are not merged. When the flow
   * is created is is created under the original customer but we cannot use that customer
   * token to find the active customer as they are not associated. We do not know the customer
   * token until we attempt to resolve the requirement.
   */
  claimable_customer_token?: string;
  /** Indicates if the customer is confirming the payment from web. */
  confirmed_from_web?: boolean;
}

/**
 * Customer is confirming a payment using their pin or CVV.
 *
 * @deprecated
 */
export interface ConfirmPasscodeInputs {
  confirm_inputs?: ConfirmInputs;
  /** Indicates if the customer is confirming the payment from web. */
  passcode?: string;
  /** A token in lieu of a passcode. Used for biometric authentication mechanisms. */
  passcode_token?: string;
}

export interface ConfirmPaymentInputs {
  instrument_token?: string;
  accepted_fee_amount?: Money;
  passcode?: string;
  passcode_token?: string;
}

export interface SelectOptionInputs {
  /** Need to add client scenario here */
  action?: string;
}

export interface VerifyIdentityInputs {
  name?: string;
  /** Birthdate as "mm/dd/yyyy". */
  birth_date?: string;
  /** The last four digits in the customer's SSN. */
  ssn_last_four?: string;
  /** The customer's full social security number. */
  ssn?: string;
  /** The customer's address. */
  address?: GlobalAddress;
  /** The customer's ip address. */
  ip_address?: string;
}

/** Customer is verifying a phone number */
export interface PhoneVerificationInputs {
  /** The phone number being verified */
  sms_number?: string;
  /** The verification code submitted to verify the phone number */
  sms_verification_code?: string;
}

/** Customer is creating a new passcode. */
export interface PasscodeCreationInputs {
  new_passcode?: string;
  new_passcode_token?: string;
  old_passcode?: string;
  old_passcode_token?: string;
  old_instrument_token?: string;
  initiator_app_token?: string;
}

export interface PhysicalCardVerificationQrCodeInputs {
  qr_code?: string;
  /** An indicator to tell if the server should skip/resolve the verify QR code requirement. */
  skippable_missing_qr_code?: boolean;
  scenario_initiator_type?: string;
}

export interface PhysicalCardVerificationCvvAndExpirationInputs {
  cvv?: string;
  expiration?: string;
}

/** Customer is verifying their passcode. */
export interface VerifyPasscodeInputs {
  passcode?: string;
  /** A token in lieu of a passcode. Used for biometric authentication mechanisms. */
  passcode_token?: string;
}

export interface FormInputs {
  action?: string;
  /** The data entered by the customer in all input fields for this form. */
  results?: FormInputsElementResult[];
}

export interface FormInputsElementResult {
  /** The unique identifier for the element within the form for which this value was entered. */
  id?: string;
  address_result?: FormInputsElementResultAddressResult | undefined;
  text_input_result?: FormInputsElementResultTextInputResult | undefined;
  option_picker_result?: FormInputsElementResultOptionPickerResult | undefined;
  cashtag_result?: FormInputsElementResultCashtagResult | undefined;
  date_input_result?: FormInputsElementResultDateInputResult | undefined;
  money_input_result?: FormInputsElementResultMoneyInputResult | undefined;
  emoji_picker_result?: FormInputsElementResultEmojiPickerResult | undefined;
  merchant_transaction_picker_result?: FormInputsElementResultMerchantTransactionOptionPickerResult | undefined;
  webview_result?: FormInputsElementResultWebviewCallbackResult | undefined;
}

export interface FormInputsElementResultAddressResult {
  address?: GlobalAddress;
  /** True if the address has been validated by a third party service such as Google Places. */
  validated?: boolean;
}

export interface FormInputsElementResultCashtagResult {
  /** The Cashtag value entered by the customer. This should *not* include the prefix symbol. */
  cashtag?: string;
}

/**
 * For submitting the date entered by the customer.
 *
 * This date is sent as separate day/month/year components rather than an ISO 8601 string
 * so that the values exactly represent what the customer entered and there is no confusion
 * about time zones.
 */
export interface FormInputsElementResultDateInputResult {
  day?: number;
  month?: number;
  year?: number;
}

export interface FormInputsElementResultTextInputResult {
  input_field_values?: string[];
}

export interface FormInputsElementResultOptionPickerResult {
  option_id?: string;
}

export interface FormInputsElementResultMoneyInputResult {
  money?: Money;
}

export interface FormInputsElementResultEmojiPickerResult {
  selection?: string;
}

export interface FormInputsElementResultMerchantTransactionOptionPickerResult {
  /** List of the external transaction tokens corresponding to the selected auth requests. */
  selected_transactions?: string[];
}

export interface FormInputsElementResultWebviewCallbackResult {
  query_params?: FormInputsElementResultWebviewCallbackResultQueryParam[];
}

export interface FormInputsElementResultWebviewCallbackResultQueryParam {
  key?: string;
  value?: string;
}

export interface AddressInputs {
  address?: GlobalAddress;
  /** True if the address has been validated by a third party service such as Google Places. */
  validated?: boolean;
}

export interface CreateCardDesignInputs {
  signature_image_bytes?: Uint8Array;
  cashtag?: string;
  mime_type?: string;
  card_theme_token?: string;
  card_theme?: string;
  canvas_policy?: CreateCardDesignInputsCustomizationOnLargeCanvasPolicy;
  touch_data?: string;
  include_cashtag?: boolean;
  full_face?: boolean;
}

export enum CreateCardDesignInputsCustomizationOnLargeCanvasPolicy {
  ORIGINAL_CANVAS = "ORIGINAL_CANVAS",
  LARGE_CANVAS = "LARGE_CANVAS",
}

export interface RegisterSmsInputs {
  sms_number?: string;
}

export interface RegisterEmailInputs {
  email_address?: string;
}

export interface UploadFileInputs {
  /** Base64 encoded file. Used by the web app. */
  file_data?: string;
  /** Raw file bytes. Used by the native apps. */
  file_bytes?: Uint8Array;
  /** If the file is an image, the mime type of the file. */
  mime_type?: string;
  /**
   * The category of file being uploaded, such as a selfie, W2, SSN card, etc. This mirrors
   * squareup.franklin.FileCategory. TODO(jayestrella): Extract FileCategory from franklin
   */
  category?: string;
  /** Payment tokens if call is originating from a flow which has payment context. */
  payment_tokens?: string[];
  /** Transfer token if call is originating from a flow which has transfer context. */
  transfer_token?: string;
  /** Fraud payload. Used by Bouncer Integration for example. */
  fraud_payload?: string;
}

export interface VerifyGovernmentIdInputs {
  /** The payments being acted upon. */
  payment_tokens?: string[];
  /** The transfer being acted upon. */
  transfer_token?: string;
  /**
   * The client recognizes the front and back and sends the image to the server
   * for extra validation.
   */
  front_image_bytes?: Uint8Array;
  back_image_bytes?: Uint8Array;
  mime_type?: string;
  /**
   * The text recognized from the PDF-417 barcode on the back of the DL.
   * If not set, the client leaves recognition completely to the server.
   */
  pdf_417_text?: string;
  /**
   * This is a franklin DB id of browser_interaction. It is typically an anti-pattern,
   * but currently necessarily due to lack of piping around request context.
   */
  browser_interaction_id?: number;
}

export interface NameOnCardInputs {
  name_on_card?: string;
}

export interface UploadCheckAmountAndImagesInputs {
  front_image_bytes?: Uint8Array;
  back_image_bytes?: Uint8Array;
  amount?: Money;
  idempotence_token?: string;
}

export interface RatePlanInput {
  rate_plan?: string;
  intended_usage?: string;
  commit?: boolean;
}

export interface LinkCardInput {
  card?: LinkCardInputPlasmaKeyedCard;
  ocr_context?: string;
  cash_instrument_type?: string;
  app_token?: string;
  ip_address?: string;
  treatment?: string;
  browser_interaction?: BrowserInteraction;
}

/** Represents a debit or credit card & related information. */
export interface LinkCardInputPlasmaKeyedCard {
  /**
   * The card's account number.
   *
   * @deprecated
   */
  unencrypted_pan?: string;
  /** An expiration date in YYMM format. */
  expiration?: string;
  /** The card's CVV number. */
  security_code?: string;
  /** The postal code of the card's billing address. */
  postal_code?: string;
  /** The last four digits of the pan. */
  last_four?: string;
  /** The type of payment instrument, such as Visa or MasterCard. */
  instrument_type?: InstrumentType;
  /** The fidelius token for the card's PAN. */
  pan_fidelius_token?: string;
  /** The cardholder name, as reviewed & submitted by the customer. */
  cardholder_name?: string;
  /** The result of an OCR scan of a credit/debit card using the Bouncer SDK. */
  bouncer_scan_result?: CardScanResult;
}

export interface LinkCardV2Inputs {
  /** Propogate instrument to requirement handler to populate end result */
  instrument?: Instrument;
  /**
   * Statuses for requirement handler to determine how to resolve requirement
   *
   * @deprecated
   */
  status?: LinkCardV2InputsStatus;
  /** Optional challenge token provided if 3DS challenge is required */
  threeds_challenge_token?: string;
  /** Link Result for requirement handler to determine how to resolve requirement */
  link_result?: LinkResult;
}

export enum LinkCardV2InputsStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
  THREEDS_CHALLENGE_REQUIRED = "THREEDS_CHALLENGE_REQUIRED",
}

export interface AmountInput {
  amount_result?: AmountInputAmountResult | undefined;
  percentage_result?: AmountInputPercentageResult | undefined;
  action?: string;
}

export interface AmountInputAmountResult {
  amount?: Money;
}

export interface AmountInputPercentageResult {
  percentage?: ScientificNumber;
}

export interface SignatureInput {
  signature_image_bytes?: Uint8Array;
  mime_type?: string;
}

export interface SelectSponsorsInputs {
  /**
   * upon cancellation, client does not set this field since the user intends to reselect a sponsor;
   * upon selection AND confirmation of sponsors, selected sponsors are passed through this field
   *
   * @deprecated
   */
  sponsors?: UiCustomer[];
  action?: SelectSponsorsInputsAction;
  /**
   * upon cancellation, the invite_code is needed to cancel previously created invitation for OON
   * request
   * upon selection of sponsors, this can be null as invite_code will not have been created yet
   */
  invite_code?: string;
  /**
   * This field indicates that the customer has allowed cashapp to pull their contacts and populate
   * them as part of the sponsor options in the select sponsors screen
   */
  dependent_contacts_syncd?: boolean;
  /** DO NOT USE YET! This field will eventually replace `sponsors` field */
  selected_sponsors?: SelectSponsorsInputsSelectedSponsor[];
}

/** Actions that can be handled between SelectSponsors and SelectSponsorsDetail blockers */
export enum SelectSponsorsInputsAction {
  /** SELECT - action sent when sponsor is selected from SelectSponsors screen to present SelectSponsorsDetail confirmation screen */
  SELECT = "SELECT",
  /** CANCEL - action sent when sponsor selection is canceled from SelectSponsorsDetail confirmation screen */
  CANCEL = "CANCEL",
  /** CONFIRM - action sent when sponsor selection is confirmed from SelectSponsorsDetail confirmation screen */
  CONFIRM = "CONFIRM",
}

/**
 * SelectedSponsor will hold not just the sponsor UICustomer object but also attributes like
 * is_in_contacts and is_added_to_favorites of the relationships between the sponsor and the dependent
 * from whom the request originated
 */
export interface SelectSponsorsInputsSelectedSponsor {
  sponsor?: UiCustomer;
  is_in_contacts?: boolean;
  is_added_to_favorites?: boolean;
}

/** Payroll provider authentication AppApis will populate this, transferring payload from the mobile client request to this object when submitting data to Plasma. */
export interface FinishPayrollProviderAuthenticationInputs {
  /**
   * The Result suffix is confusing since plasma uses the EndResult suffix for defining
   * Result objects stored in the flow history. See RequirementResultProto.proto for examples.
   *
   * @deprecated
   */
  pinwheel_link_result?: FinishPayrollProviderAuthenticationInputsPinwheelLinkResult | undefined;
  pinwheel_link_inputs?: FinishPayrollProviderAuthenticationInputsPinwheelLinkInputs | undefined;
}

/** Deprecate in favor of PinwheelLinkInputs */
export interface FinishPayrollProviderAuthenticationInputsPinwheelLinkResult {
  link_token?: string;
  on_login_response?: PinwheelLinkOnLoginResponse | undefined;
  on_exit_response?: PinwheelLinkOnExitResponse | undefined;
}

/** Populate this object if the mobile client request contained payload from a Pinwheel user authentication. */
export interface FinishPayrollProviderAuthenticationInputsPinwheelLinkInputs {
  link_token?: string;
  on_login_response?: PinwheelLinkOnLoginResponse | undefined;
  on_exit_response?: PinwheelLinkOnExitResponse | undefined;
  on_success_response?:
    | PinwheelLinkOnSuccessResponse
    | undefined;
  /** Populate using header value from mobile request. Example value: "America/Los_Angeles" */
  timezone_location?: string;
  /** Needed temporarily to transition instant-pay users to new loading blocker */
  appVersion?: AppVersion;
}

export interface PasswordCreationInputs {
  password?: string;
}

export interface PasswordVerificationInputs {
  password?: string;
  enclave?: string;
}

export interface SearchPayrollProvidersInputs {
  query?: string;
  selected_payroll_provider?: PayrollProvider | undefined;
  selected_fallback_result?: boolean | undefined;
}

export interface FinishInstantPayAutoAdvanceInputs {
  requested_to_be_notified?: boolean;
}

export interface SelectShippingOptionInputs {
  shipping_info?: SelectShippingOptionInputsShippingInfo;
}

export interface SelectShippingOptionInputsShippingInfo {
  shipping_option?: SelectShippingOptionInputsShippingInfoShippingOption;
  price?: Money;
}

export enum SelectShippingOptionInputsShippingInfoShippingOption {
  STANDARD = "STANDARD",
  TWO_DAY = "TWO_DAY",
}

export interface ConfigureRoundUpInputs {
  automation?: Automation;
}

export interface ConfigureIWYSInputs {
  automation?: Automation;
}

/** Sent by the apps once the Persona dIDV inquiry, launched by PersonaDidvBlocker, has completed. */
export interface PersonaDidvInputs {
  /** Indicates the state of completion of the inquiry */
  inquiry_response?: PersonaDidvInputsInquiryResponse;
  /** The created inquiry identifier */
  inquiry_id?: string;
  /** Should be failed or completed, indicates whether all parts of the inquiry were completed */
  status?: string;
  /** If inquiry_response is Cancelled, this identifier can later be used to resume the inquiry */
  session_token?: string;
  /** If inquiry_response is Error, the message contains why */
  debug_message?: string;
  /** The franklin DB id of browser_interaction which contains client information like IP, cookies, and headers */
  browser_interaction_id?: number;
}

export enum PersonaDidvInputsInquiryResponse {
  /** ERROR - Something went wrong with the inquiry flow (e.g. template was misconfigured, network errors) */
  ERROR = "ERROR",
  /** CANCEL - The inquiry was abandoned by the user */
  CANCEL = "CANCEL",
  /** COMPLETE - The inquiry flow completed */
  COMPLETE = "COMPLETE",
}

/** Sent by the apps when a customer taps something in an ActivityPickerBlocker. */
export interface ActivityPickerInputs {
  /** The id of the entity the customer picked. Should be prefixed according to go/cashtokens. */
  entity_id?: string;
}

export interface ProvisionDigitalWalletTokenInputs {
  provisioning_app_version?: string;
  digital_wallet_issuer?: string;
  issued_card_token?: string;
  apple_pay_request?: ProvisionDigitalWalletTokenInputsApplePayInputs | undefined;
  google_pay_request?: ProvisionDigitalWalletTokenInputsGooglePayInputs | undefined;
}

export interface ProvisionDigitalWalletTokenInputsApplePayInputs {
  certificates?: Uint8Array[];
  nonce?: Uint8Array;
  nonce_signature?: Uint8Array;
}

export interface ProvisionDigitalWalletTokenInputsGooglePayInputs {
  wallet_account_id?: string;
  device_id?: string;
}

export interface ActivateDigitalWalletTokenInputs {
  digital_wallet_cards?: ActivateDigitalWalletTokenInputsDigitalWalletCard[];
}

/** Representation of an issued card instance in a digital wallet. */
export interface ActivateDigitalWalletTokenInputsDigitalWalletCard {
  /** The issued card token associated with this card. */
  issued_card_token?: string;
  /** Identifier of the issued card. This is shared between wallets. */
  fpan_id?: string;
  /** Identifier for the card instance on the device. */
  dpan_id?: string;
}

export interface InstrumentSelectionInputs {
  instrument_selection_option?: InstrumentSelectionOption;
}

/** Sent by the apps when a customer submits a FilesetUploadBlocker. */
export interface FilesetUploadInputs {
  /** The identifier of the `BlockerAction` that the user tapped to submit. */
  action_id?: string;
  /** The tokens of the files being submitted. */
  file_tokens?: string[];
}

export interface RewardCodeInput {
  reward_code?: string;
}

export interface SkipInput {
}

export interface WebViewCallbackInputs {
  query_params?: WebViewCallbackInputsQueryParam[];
}

export interface WebViewCallbackInputsQueryParam {
  key?: string;
  value?: string;
}

export interface EndFlowInputs {
  action_id?: string;
}

export interface ThreeDomainSecureRedirectInputs {
  transaction_id?: string;
  transaction_type?: string;
  /** Redirect URL with the app scheme provided by 3DS client component * */
  redirect_url?: string;
  /** CVV * */
  security_code?: string;
  /** Linking a card requires browser interaction and the customer's IP address. * */
  browser_interaction?: BrowserInteraction;
  ip_address?: string;
}

export interface ThreeDomainSecureNativeInputs {
  transaction_id?: string;
  transaction_type?: string;
  success?: ThreeDomainSecureV2ActionSuccess | undefined;
  error?:
    | ThreeDomainSecureV2ActionError
    | undefined;
  /** Linking a card requires browser interaction and the customer's IP address. * */
  browser_interaction?: BrowserInteraction;
  ip_address?: string;
}

export interface InstantPayDirectDepositTransferSelectionInputs {
  account_transfer_selection?: DirectDepositTransferSelectionRequestAccountTransferSelection;
}

export interface ParticipantSelectionInputs {
  participants?: GroupParticipant[];
}

export interface MultiCurrencyAmountEntryBlockerInputs {
  /** required (Money(amount = 200_00, currency_code = USD)) */
  sender_amount?: Money;
  /** required (Money(amount = 4128_78, currency_code = MXN)) */
  receiver_amount?: Money;
  /** required (SENDER) */
  focused_field?: MultiCurrencyAmountEntryBlockerFocusedCurrencyField;
  /** required (opaque token from blocker reflected back) */
  cost_tier_identifier?: string;
  /**
   * what fees were actually displayed to the customer.
   * Blended fees should be excluded here / set as $0 amount
   */
  displayed_fees?: MultiCurrencyAmountEntryBlockerInputsFee[];
  /** required (the identifier of the `BlockerAction` that the initiator tapped to submit) */
  action_id?: string;
  /** optional (description of the transaction provided by the initiator) */
  note_value?: string;
  /** optional (the initiator's selected instrument) */
  instrument_token?: string;
}

export interface MultiCurrencyAmountEntryBlockerInputsFee {
  /** required actual displayed fee amount (Money(amount = 199, currency_code = USD)) */
  amount?: Money;
  /** required, specifies which fee was displayed */
  identifier?: string;
}

export interface ClabeEntryInputs {
  /** clabe bank account number */
  clabe_number?:
    | string
    | undefined;
  /** fidelius token linked to user's bank account */
  clabe_token?: string | undefined;
}

export interface SetRecurringPaymentPreferencesInputs {
  action?: RecurringPaymentBlockerButtonAction;
  schedule?: RecurringPaymentBlockerSchedule;
  amount?: Money;
}

export interface OverflowOptionPickerBlockerInputs {
  selected_option_identifier?: string;
}

export interface MultiCurrencyPaymentReviewInputs {
  selected_action?: MultiCurrencyPaymentReviewInputsAction;
}

export enum MultiCurrencyPaymentReviewInputsAction {
  EDIT_AMOUNT = "EDIT_AMOUNT",
  EDIT_RECIPIENT_INFO = "EDIT_RECIPIENT_INFO",
  EDIT_DELIVERY_METHOD = "EDIT_DELIVERY_METHOD",
  ACCEPT = "ACCEPT",
}

export interface RecipientSelectorInputs {
  /** required, the selected customer */
  recipient?: UiCustomer;
  /** optional, the selected instrument for the transaction, including potential fees */
  instrument_selection?: InstrumentSelection;
  /** optional, the value of the note text field populated by the initiator */
  note?: string;
  /** required, an id which provides idempotency if a request is retried */
  external_id?: string;
}

/** Blocker inputs for the SelectInvestmentEntity Requirement */
export interface SelectInvestmentEntityInputs {
  /** The token of the investment entity the customer has selected */
  investment_entity_token?: string;
  is_cancelled?: boolean;
}
